import { Intercom } from "@intercom/messenger-js-sdk";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  Link,
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useRouteError
} from "react-router-dom";

import { getErrorMessage } from "@repo/system";
import { T, tx, useT } from "@repo/transifex";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";

import { Button } from "./components/Button";
import { ModalErrorGeneric } from "./components/kiosk/ui/modals/error/modal-error-generic";
import { LayoutError } from "./components/Layout";
import { ModalProvider } from "./components/modal/modal-context";
import { Toaster } from "./components/ui/toaster";
import { authActions } from "./providers/store/auth/actions";
import { useAuth, useAuthStatus } from "./providers/store/auth/hooks";
import { NotFoundPage } from "./routes/*/route";
import { CollectOperatorPage } from "./routes/__app.collect.operator/route";
import { CollectChildOrderPage } from "./routes/__app.collect.orders/route";
import { CollectLayoutPage } from "./routes/__app.collect/route";
import { DiningOptionsPage } from "./routes/__app.pos.dining-options/route";
import { EmployeeCardErrorPage } from "./routes/__app.pos.employee-card.error/route";
import { EmployeeCardPage } from "./routes/__app.pos.employee-card/route";
import { HomePage } from "./routes/__app.home/route";
import { KioskLandingPage } from "./routes/__app.kiosk._index/route";
import { KioskBasketPaymentPage } from "./routes/__app.kiosk.basket.payment/route";
import { KioskBasketPaymentFreedomPayPage } from "./routes/__app.kiosk.basket.freedompay/route";
import { KioskBasketPage } from "./routes/__app.kiosk.basket/route";
import { KioskEmailPage } from "./routes/__app.kiosk.order-confirmation.email/route";
import { KioskPrintPage } from "./routes/__app.kiosk.order-confirmation.print/route";
import { KioskBasketOrderConfirmationPage } from "./routes/__app.kiosk.order-confirmation/route";
import { KioskProductDetailLayoutPage } from "./routes/__app.kiosk.products.$productId/route";
import { KioskProductDetailsPage } from "./routes/__app.kiosk.products.$productId._index/route";
import { KioskWeightProductPage } from "./routes/__app.kiosk.products.$productId.weight/route";
import { KioskAdminPage } from "./routes/__app.kiosk.admin/route";
import { KioskInactivePage } from "./routes/__app.kiosk.products.inactive/route";
import { KioskProductsRoute } from "./routes/__app.kiosk.products/route";
import { KioskLayoutPage } from "./routes/__app.kiosk/route";
import { PosEmailReceiptPage } from "./routes/__app.pos.sell.email/route";
import { PosPrintReceiptPage } from "./routes/__app.pos.sell.print/route";
import { DialogBasketProduct } from "./routes/__app.pos.sell.basket-product.$basketProductId/route";
import { PosBillingPage } from "./routes/__app.pos.sell.billing/route";
import { PosCardV3Page } from "./routes/__app.pos.sell.card/route";
import { PosCashPage } from "./routes/__app.pos.sell.cash/route";
import { PosCreditPage } from "./routes/__app.pos.sell.credit/route";
import { CustomProducts } from "./routes/__app.pos.sell.custom-product/route";
import { DiscountOrderPage } from "./routes/__app.pos.sell.discount-order/route";
import { InvoicePage } from "./routes/__app.pos.sell.invoice/route";
import { PosMobilePay } from "./routes/__app.pos.sell.mobilepay/route";
import { ShopperNamePage } from "./routes/__app.pos.sell.shopper-name/route";
import { StampcardPage } from "./routes/__app.pos.sell.stampcard/route";
import { PosSwishPage } from "./routes/__app.pos.sell.swish/route";
import { WeightPage } from "./routes/__app.pos.sell.weight-product.$productId/route";
import { PosLayout } from "./routes/__app.pos/route";
import { VariantsPage } from "./routes/__app.pos.product.$productId/route";
import { SelectorsPage } from "./routes/__app.pos.selectors/route";
import { PrintersPage } from "./routes/__app.settings.devices.printers/route";
import { ScalesPage } from "./routes/__app.settings.devices.scales/route";
import { ScalePrecisionPage } from "./routes/__app.settings.devices.scales.precision/route";
import { TerminalsPage } from "./routes/__app.settings.devices.terminals/route";
import { DevicesPage } from "./routes/__app.settings.devices/route";
import { SessionReportsPage } from "./routes/__app.settings.reports.$tillId.$sessionId/route";
import { SessionTillsPage } from "./routes/__app.settings.reports.$tillId/route";
import { ReportsPage } from "./routes/__app.settings.reports/route";
import { TillPage } from "./routes/__app.settings.tills.$tillId/route";
import { TillSessionPage } from "./routes/__app.settings.tills.$tillId_.$sessionId/route";
import { TillSessionClosePage } from "./routes/__app.settings.tills.$tillId_.$sessionId_.close/route";
import { TillSessionTransactionPage } from "./routes/__app.settings.tills.$tillId_.$sessionId_.transactions/route";
import { TillSessionOpenPage } from "./routes/__app.settings.tills.$tillId_.open/route";
import { TillsPage } from "./routes/__app.settings.tills._index/route";
import { TransactionsPage } from "./routes/__app.settings.transactions/route";
import { SettingsPage } from "./routes/__app.settings/route";
import { SimpleCheckoutPage } from "./routes/__app.simple-checkout.index/route";
import { SimpleCheckoutLayoutPage } from "./routes/__app.simple-checkout/route";
import { TicketValidationHelpPage } from "./routes/__app.ticket-validation.help/route";
import { TicketValidationPage } from "./routes/__app.ticket-validation.index/route";
import { TicketValidationLayoutPage } from "./routes/__app.ticket-validation/route";
import { AppLayout } from "./routes/__app/route";
import { AdminPage } from "./routes/admin/route";
import { LicensePage } from "./routes/license/route";
import { OfflinePage } from "./routes/offline/route";
import { ProfilePinPage } from "./routes/profiles.$profileId/route";
import { ProfilesPage } from "./routes/profiles/route";
import { LoginPage } from "./routes/root/route";
import { SupportPage } from "./routes/support/route";
import { logger } from "./services/logger";
import { INTERCOM_APP_ID } from "./utils/env";
import { setSentryTagSession, setSentryTagTill } from "./utils/sentry";
import { PosIndexPage } from "./routes/__app.pos._index/route";
import { PosSellPage } from "./routes/__app.pos.sell/route";
import { PosChildrenSearchPage } from "./routes/__app.pos.search-children/route";
import { PosSearchGroupPage } from "./routes/__app.pos.search-group/route";
import { CollectChildOrderCreatePage } from "./routes/__app.collect.orders.create._index/route";
import { CollectChildOrderCreateLayoutPage } from "./routes/__app.collect.orders.create/route";
import { CollectChildOrderCreateProductPage } from "./routes/__app.collect.orders.create.products.$productId/route";

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { pathname } = useLocation();
  const status = useAuthStatus();

  if (status === "none") {
    return <Navigate replace to="/" />;
  }

  if (
    status === "user" &&
    !pathname.includes("license") &&
    pathname !== "/admin"
  ) {
    return <Navigate replace to="/license" />;
  }

  if (
    status === "till" &&
    !pathname.includes("profiles") &&
    pathname !== "/admin"
  ) {
    return <Navigate replace to="/profiles" />;
  }

  return children;
};

const ErrorPage = ({ errorMessage }: { errorMessage: string }) => {
  const t = useT();

  return (
    <LayoutError>
      <div className="flex flex-col gap-2 text-center">
        <T _str={`${t("An error occurred")}: ${errorMessage}`} />

        <Link to="/home">
          <Button>
            <T _str="Go to Home" />
          </Button>
        </Link>

        <Button
          onClick={() => {
            location.reload();
          }}
          variant="info"
        >
          <T _str="Reload" />
        </Button>

        <Button
          onClick={() => {
            authActions.logout();
          }}
          variant="danger"
        >
          <T _str="Log out" />
        </Button>
      </div>
    </LayoutError>
  );
};

const RootLayout = () => {
  const { userId, tillId, schoolId, partnerId } = useAuth();
  const { data: till } = useCurrentTill();
  const { language, isDefault } = useCurrentTillLocalisation();

  if (INTERCOM_APP_ID && userId) {
    Intercom({
      app_id: INTERCOM_APP_ID,
      region: "eu",
      hide_default_launcher: true,
      user_id: userId,
      till_id: tillId,
      location_id: schoolId,
      partner_id: partnerId
    });
  }

  useEffect(() => {
    const browserLanguage = window.navigator.language?.split("-")[0];
    try {
      tx.setCurrentLocale(isDefault ? browserLanguage ?? language : language);
    } catch (error) {
      logger.error(error as Error, "[Transifex] failed to set current locale");
    }
  }, [language, isDefault]);

  // Identify till in Sentry
  useEffect(() => {
    if (till) {
      setSentryTagTill(till.id);
      setSentryTagSession(till.sessionId);
    }
  }, [till]);

  return (
    <ModalProvider>
      <div className="h-screen w-full">
        <Outlet />
        <Toaster />
      </div>
    </ModalProvider>
  );
};

const ErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    if (error instanceof Error) {
      logger.error(error, `[UNEXPECTED_ERROR] ${getErrorMessage(error)}`);
    }
  }, [error]);

  return <ErrorPage errorMessage={getErrorMessage(error)} />;
};

const KioskErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const resetError = () => {
    navigate(".", { replace: true });
  };

  useEffect(() => {
    if (error instanceof Error) {
      logger.error(error, `[KIOSK_UNEXPECTED_ERROR] ${getErrorMessage(error)}`);
    }
  }, [error]);

  return <ModalErrorGeneric onClose={resetError} />;
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router: ReturnType<typeof sentryCreateBrowserRouter> =
  sentryCreateBrowserRouter([
    {
      path: "/",
      element: <RootLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: <LoginPage />
        },
        {
          path: "admin",
          element: (
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          )
        },
        {
          path: "license",
          element: (
            <ProtectedRoute>
              <LicensePage />
            </ProtectedRoute>
          )
        },
        {
          path: "profiles",
          element: (
            <ProtectedRoute>
              <ProfilesPage />
            </ProtectedRoute>
          ),
          children: [
            {
              path: ":profileId",
              element: <ProfilePinPage />
            }
          ]
        },
        {
          element: (
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          ),
          children: [
            {
              path: "home",
              element: <HomePage />
            },
            {
              path: "support",
              element: <SupportPage />
            },

            {
              path: "settings",
              element: <SettingsPage />,
              children: [
                {
                  path: "devices",
                  element: <DevicesPage />,
                  children: [
                    {
                      path: "printers",
                      element: <PrintersPage />
                    },
                    {
                      path: "scales",
                      element: <ScalesPage />
                    },
                    {
                      path: "scales/precision",
                      element: <ScalePrecisionPage />
                    },
                    {
                      path: "terminals",
                      element: <TerminalsPage />
                    }
                  ]
                },
                {
                  path: "reports",
                  element: <ReportsPage />,
                  children: [
                    {
                      path: ":tillId",
                      element: <SessionTillsPage />,
                      children: [
                        {
                          path: ":sessionId",
                          element: <SessionReportsPage />
                        }
                      ]
                    }
                  ]
                },
                {
                  path: "tills",
                  element: <TillsPage />
                },
                {
                  path: "tills/:tillId",
                  element: <TillPage />
                },
                {
                  path: "tills/:tillId/open",
                  element: <TillSessionOpenPage />
                },
                {
                  path: "tills/:tillId/:sessionId",
                  element: <TillSessionPage />
                },
                {
                  path: "tills/:tillId/:sessionId/close",
                  element: <TillSessionClosePage />
                },
                {
                  path: "tills/:tillId/:sessionId/transactions",
                  element: <TillSessionTransactionPage />
                },
                {
                  path: "transactions",
                  element: <TransactionsPage />
                }
              ]
            },
            {
              path: "collect",
              element: <CollectLayoutPage />,
              children: [
                {
                  index: true,
                  element: <CollectOperatorPage />
                },
                {
                  path: "orders",
                  element: <CollectChildOrderPage />
                },
                {
                  path: "orders/create",
                  element: <CollectChildOrderCreateLayoutPage />,
                  children: [
                    {
                      index: true,
                      element: <CollectChildOrderCreatePage />
                    },
                    {
                      path: "products/:productId",
                      element: <CollectChildOrderCreateProductPage />
                    }
                  ]
                }
              ]
            },
            {
              path: "kiosk",
              element: <KioskLayoutPage />,
              errorElement: <KioskErrorBoundary />,
              children: [
                {
                  index: true,
                  element: <KioskLandingPage />
                },
                {
                  path: "admin",
                  element: <KioskAdminPage />
                },
                {
                  path: "basket",
                  element: <KioskBasketPage />,
                  children: [
                    {
                      path: "inactive",
                      element: <KioskInactivePage />
                    },
                    {
                      path: "payment",
                      element: <KioskBasketPaymentPage />
                    },
                    {
                      path: "freedompay",
                      element: <KioskBasketPaymentFreedomPayPage />
                    }
                  ]
                },
                {
                  path: "products",
                  element: <KioskProductsRoute />,
                  children: [
                    {
                      path: ":path?/inactive",
                      element: <KioskInactivePage />
                    },
                    {
                      path: ":productId",
                      element: <KioskProductDetailLayoutPage />,
                      children: [
                        {
                          index: true,
                          element: <KioskProductDetailsPage />
                        },
                        {
                          path: "weight",
                          element: <KioskWeightProductPage />
                        },
                        {
                          path: "weight/inactive",
                          element: <KioskInactivePage />
                        }
                      ]
                    },
                    {
                      path: "admin",
                      element: <KioskAdminPage />
                    }
                  ]
                },
                {
                  path: "order-confirmation",
                  element: <KioskBasketOrderConfirmationPage />
                },
                {
                  path: "order-confirmation/print",
                  element: <KioskPrintPage />
                },
                {
                  path: "order-confirmation/email",
                  element: <KioskEmailPage />
                }
              ]
            },
            {
              path: "ticket-validation",
              element: <TicketValidationLayoutPage />,
              children: [
                {
                  index: true,
                  element: <TicketValidationPage />
                },
                {
                  path: "help",
                  element: <TicketValidationHelpPage />
                }
              ]
            },
            {
              path: "simple-checkout",
              element: <SimpleCheckoutLayoutPage />,
              children: [
                {
                  index: true,
                  element: <SimpleCheckoutPage />
                }
              ]
            },
            {
              path: "pos",
              element: <PosLayout />,
              children: [
                {
                  index: true,
                  element: <PosIndexPage />
                },
                {
                  path: "search-children",
                  element: <PosChildrenSearchPage />
                },
                {
                  path: "sell",
                  element: <PosSellPage />,
                  children: [
                    {
                      path: "billing",
                      element: <PosBillingPage />
                    },
                    {
                      path: "cash",
                      element: <PosCashPage />
                    },
                    {
                      path: "card",
                      element: <PosCardV3Page />
                    },
                    {
                      path: "credit",
                      element: <PosCreditPage />
                    },
                    {
                      path: "mobilepay",
                      element: <PosMobilePay />
                    },
                    {
                      path: "swish",
                      element: <PosSwishPage />
                    },
                    {
                      path: "email",
                      element: <PosEmailReceiptPage />
                    },
                    {
                      path: "print",
                      element: <PosPrintReceiptPage />
                    },
                    {
                      path: "custom-product",
                      element: <CustomProducts />
                    },
                    {
                      path: "shopper-name",
                      element: <ShopperNamePage />
                    },
                    {
                      path: "invoice",
                      element: <InvoicePage />
                    },
                    {
                      path: "discount-order",
                      element: <DiscountOrderPage />
                    },
                    {
                      path: "basket-product/:basketProductId",
                      element: <DialogBasketProduct />
                    },
                    {
                      path: "weight-product/:productId",
                      element: <WeightPage />
                    },
                    {
                      path: "stampcard",
                      element: <StampcardPage />
                    }
                  ]
                },
                {
                  path: "product/:productId",
                  element: <VariantsPage />
                },
                {
                  path: "dining-options",
                  element: <DiningOptionsPage />
                },
                {
                  path: "employee-card",
                  element: <EmployeeCardPage />
                },
                {
                  path: "employee-card/error",
                  element: <EmployeeCardErrorPage />
                },
                {
                  path: "selectors",
                  element: <SelectorsPage />
                },
                {
                  path: "search-group",
                  element: <PosSearchGroupPage />
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: "*",
      element: <NotFoundPage />
    },
    {
      path: "offline",
      element: <OfflinePage />
    }
  ]);

export { router };
